import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { BsDatabase, BsDisplay, BsBraces, BsFileText, BsMortarboard, BsX } from 'react-icons/bs';
import './AssetTypeModal.css';

export type ModalMode = 'explore' | 'publish';

const ASSET_TYPES = [
    { icon: <BsDatabase />, color: 'rgb(239,43,137)', label: 'Datasets', desc: 'Geospatial, climate, environmental and EO data ready to explore and integrate into your workflows.', exploreUrl: '/catalog/dataset', publishUrl: '/form/dataset' },
    { icon: <BsDisplay />, color: 'rgb(20,143,150)', label: 'Applications', desc: 'Ready-to-run tools, dashboards and services built on top of scientific data and models.', exploreUrl: '/catalog/application', publishUrl: '/form/application' },
    { icon: <BsBraces />, color: 'rgb(123,52,219)', label: 'Models', desc: 'Trained ML models, simulation frameworks and algorithms for Earth system analysis.', exploreUrl: '/catalog/model', publishUrl: '/form/model' },
    { icon: <BsFileText />, color: 'rgb(247,164,56)', label: 'Documents', desc: 'Scientific papers, technical reports and methodologies linked to assets.', exploreUrl: '/catalog/paper', publishUrl: '/form/paper' },
    { icon: <BsMortarboard />, color: 'rgb(99,193,224)', label: 'Courses', desc: 'Tutorials, guided notebooks and learning material to get started with DestinE tools.', exploreUrl: '/catalog/course', publishUrl: '/form/course' },
];

type Props = { mode: ModalMode | null; onClose: () => void };

export default function AssetTypeModal({ mode, onClose }: Props) {
    const navigate = useNavigate();

    useEffect(() => {
        if (!mode) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [mode, onClose]);

    if (!mode) return null;

    return createPortal(
        <div className="home-modal-overlay" onClick={onClose}>
            <div className="home-modal" onClick={e => e.stopPropagation()}>
                <div className="home-modal-header">
                    <h2 className="home-modal-title">
                        {mode === 'explore' ? 'What do you want to explore?' : 'What do you want to publish?'}
                    </h2>
                    <button className="home-modal-close" onClick={onClose}><BsX /></button>
                </div>
                <div className="home-modal-grid">
                    {ASSET_TYPES.map((t, i) => (
                        <button
                            key={i}
                            className="home-modal-card"
                            style={{ '--at-color': t.color } as React.CSSProperties}
                            onClick={() => { navigate(mode === 'explore' ? t.exploreUrl : t.publishUrl); onClose(); }}
                        >
                            <div className="home-modal-card-icon">{t.icon}</div>
                            <div className="home-modal-card-label">{t.label}</div>
                            <div className="home-modal-card-desc">{t.desc}</div>
                        </button>
                    ))}
                </div>
            </div>
        </div>,
        document.body
    );
}
